import React, { useEffect, useMemo, useRef, useState } from 'react';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import AdminActionGroup from '../components/AdminActionGroup';
import { fetchUsers } from '../services/user';
import { getStudentSummaryAdmin } from '../services/analytics';

const PrintStyles = () => (
  <style>{`
    @media print {
      body { background: #fff !important; }
      .no-print { display: none !important; }
      .report-sheet { width: 100%; padding: 0; box-shadow: none !important; }
      .report-card { break-inside: avoid; box-shadow: none !important; border: 1px solid #e5e7eb; }
    }
    .report-sheet { max-width: 960px; margin: 0 auto; padding: 24px; background: #fff; }
    .report-title { font-size: 1.6rem; color: #00154A; font-weight: 800; letter-spacing: .2px; }
    .report-meta { color: #64748b; font-size: .9rem; }
    .report-card { background:#fff; border-radius: 18px; box-shadow: 0 5px 12px rgba(0,0,0,.06); padding: 16px; }
    .stat { font-size: 1.6rem; font-weight: 800; color:#0b245e; }
    .muted { color:#64748b; }
  `}</style>
);

const Reports = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const sheetRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        const all = await fetchUsers('student');
        if (isMounted) {
          setStudents(all);
        }
      } catch (e) {
        if (isMounted) {
          setError(e?.message || 'Failed to load students');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const selectedStudent = useMemo(
    () => students.find((student) => student._id === selectedId),
    [students, selectedId]
  );

  const loadReport = async () => {
    if (!selectedId) return;
    try {
      setBusy(true);
      setError('');
      setReport(null);
      const data = await getStudentSummaryAdmin(selectedId);
      setReport(data);
    } catch (e) {
      setError(e?.message || 'Failed to load report');
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = () => {
    window.print();
  };

  const studentOptions = useMemo(
    () =>
      students.map((student) => ({
        value: student._id,
        label: `${student.name || 'Unnamed'} ${student.surname || ''}`.trim(),
        email: student.email,
      })),
    [students]
  );

  return (
    <DashboardLayout>
      <PrintStyles />
      <Stack spacing={3} className="no-print">
        <Card>
          <CardHeader
            title={
              <Stack direction="row" spacing={1.5} alignItems="center">
                <AssessmentRoundedIcon color="primary" />
              </Stack>
            }
            subheader="Generate learner performance summaries."
          />
          <Divider />
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <FormControl fullWidth sx={{ minWidth: { md: 280 } }}>
                <InputLabel id="student-select-label">Student</InputLabel>
                <Select
                  labelId="student-select-label"
                  label="Student"
                  value={selectedId}
                  onChange={(event) => setSelectedId(event.target.value)}
                  disabled={loading || busy}
                >
                  <MenuItem value="">
                    <em>Select a student…</em>
                  </MenuItem>
                  {studentOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack direction="column" spacing={0.25}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.label || option.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                {!selectedId ? <FormHelperText>Select a student to enable report actions.</FormHelperText> : null}
              </FormControl>

              <AdminActionGroup>
                <Button
                  variant="contained"
                  startIcon={busy ? <RefreshRoundedIcon /> : <PlayArrowRoundedIcon />}
                  onClick={loadReport}
                  disabled={!selectedId || busy}
                >
                  {busy ? 'Generating…' : 'Generate'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ClearRoundedIcon />}
                  onClick={() => setReport(null)}
                  disabled={busy || !report}
                >
                  Clear
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdfRoundedIcon />}
                  onClick={downloadPdf}
                  disabled={!report}
                >
                  Download PDF
                </Button>
              </AdminActionGroup>
            </Stack>
          </CardContent>
          {(loading || busy) && <LinearProgress />}
        </Card>

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}
      </Stack>

      {report && (
        <Paper ref={sheetRef} className="report-sheet" sx={{ mt: { xs: 2, md: 4 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ mb: 3 }}
          >
            <Stack spacing={0.75}>
              <Typography className="report-title">Learner Performance Report</Typography>
              <Typography className="report-meta">Generated: {new Date().toLocaleString()}</Typography>
              <Typography className="report-meta">
                Student: {report.student?.name || selectedStudent?.name || 'Unknown'} ({
                  report.student?.email || selectedStudent?.email || 'No email'
                })
              </Typography>
            </Stack>
            <Box component="img" src="/logo.png" alt="Logo" sx={{ width: 72, height: 72 }} />
          </Stack>

          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Paper className="report-card" sx={{ height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Overall Sessions
                </Typography>
                <Typography className="stat">{report.totalSessions}</Typography>
                <Typography className="muted">Total sessions</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper className="report-card" sx={{ height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Upcoming Sessions
                </Typography>
                <Typography className="stat">{report.totalUpcomingClasses}</Typography>
                <Typography className="muted">Next 5 listed below</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper className="report-card" sx={{ height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Study Hours
                </Typography>
                <Typography className="stat">{report.totalStudyHours} hrs</Typography>
                <Typography className="muted">Cumulative</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Stack spacing={2.5}>
            <Paper className="report-card">
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Monthly Study Hours
              </Typography>
              {(!report.monthlyStudyHours || report.monthlyStudyHours.length === 0) ? (
                <Typography className="muted">No data</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Month</TableCell>
                        <TableCell>Hours</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.monthlyStudyHours.map((month) => (
                        <TableRow key={month.month}>
                          <TableCell>{month.month}</TableCell>
                          <TableCell>{month.hours}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            <Paper className="report-card">
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Upcoming Classes
              </Typography>
              {(!report.upcomingClasses || report.upcomingClasses.length === 0) ? (
                <Typography className="muted">No upcoming classes</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Subject</TableCell>
                        <TableCell>Tutor</TableCell>
                        <TableCell>Start</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.upcomingClasses.map((session) => (
                        <TableRow key={session._id}>
                          <TableCell>{session.subject || '-'}</TableCell>
                          <TableCell>{session.tutorId?.name || '-'}</TableCell>
                          <TableCell>{new Date(session.startTime).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Stack>
        </Paper>
      )}
    </DashboardLayout>
  );
};

export default Reports;
